import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  RouterOutlet,
  RouterModule,
  Router,
  NavigationEnd,
} from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, filter, map, takeUntil } from 'rxjs';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { AccountInfo } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { ToastContainerComponent } from './shared/toast';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    FormsModule,
    ToastContainerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private readonly destroy$ = new Subject<void>();

  title = 'ControlTower';

  // Fullscreen mode: hides the top nav for immersive views (e.g., log viewer)
  isFullscreen = signal(false);

  // Environment indicator
  environmentName: string = '';
  environmentBadgeClass: string = '';

  // Authentication properties
  isAuthenticated = false;
  currentUser: AccountInfo | null = null;
  isAuthLoading = true;

  // Mobile menu state
  isMobileMenuOpen = false;

  // Dropdown states
  isUserMenuOpen = false;
  isPublishMenuOpen = false;
  isCreateMenuOpen = false;
  isExtraMenuOpen = false;

  // API URL for external links (Swagger, Hangfire)
  apiUrl = environment.apiUrl || window.location.origin;

  // Search functionality
  searchQuery = '';

  // System Health
  isSystemHealthy = true;

  // Dark mode
  isDarkMode = false;

  constructor() {
    this.initializeDarkMode();
    this.initializeEnvironment();
  }

  ngOnInit(): void {
    // Guard: detect unreplaced APIM gateway URL placeholder (pipeline injection failure)
    if (environment.apiUrl.includes('__APIM_GATEWAY_URL__')) {
      console.error(
        'FATAL: APIM gateway URL was not injected at build time. API calls will fail.'
      );
    }

    // Track fullscreen routes
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => {
          let route = this.router.routerState.root;
          while (route.firstChild) route = route.firstChild;
          return route.snapshot.data['fullscreen'] === true;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(fs => this.isFullscreen.set(fs));

    // Handle MSAL redirect flow (must be called early, before guard runs)
    this.msalService.handleRedirectObservable().subscribe();

    // Listen for interaction status to track loading state
    // Only call account APIs when InteractionStatus.None (MSAL v4+ requirement)
    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        this.isAuthLoading = status !== InteractionStatus.None;
        if (status === InteractionStatus.None) {
          this.updateAuthState();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateAuthState(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    this.isAuthenticated = accounts.length > 0;
    if (accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      this.currentUser = accounts[0];
    } else {
      this.currentUser = null;
    }
  }

  initializeDarkMode(): void {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      this.isDarkMode = JSON.parse(savedTheme);
    } else {
      // Check system preference
      this.isDarkMode = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
    }
    this.applyDarkMode();
  }

  private initializeEnvironment(): void {
    // Determine environment based on envName (not production flag, since staging also uses production:true for Angular optimizations)
    const envName = environment.envName;
    if (envName.toLowerCase() === 'production') {
      // In production, don't show any badge
      this.environmentName = '';
      this.environmentBadgeClass = '';
    } else {
      // Use envName from environment configuration
      this.environmentName = envName.toUpperCase();

      // Set badge color based on environment name
      const envLower = envName.toLowerCase();
      if (envLower.includes('local')) {
        this.environmentBadgeClass = 'bg-purple-600 text-white';
      } else if (envLower.includes('adhoc')) {
        this.environmentBadgeClass = 'bg-orange-600 text-white';
      } else if (envLower.includes('staging')) {
        this.environmentBadgeClass = 'bg-yellow-600 text-white';
      } else if (envLower.includes('development')) {
        this.environmentBadgeClass = 'bg-blue-600 text-white';
      } else {
        // Default color for unknown environments
        this.environmentBadgeClass = 'bg-gray-600 text-white';
      }
    }
  }

  toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('darkMode', JSON.stringify(this.isDarkMode));
    this.applyDarkMode();
  }

  private applyDarkMode(): void {
    if (this.isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  toggleUserMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isUserMenuOpen = !this.isUserMenuOpen;
    this.isPublishMenuOpen = false;
    this.isCreateMenuOpen = false;
  }

  togglePublishMenu(): void {
    this.isPublishMenuOpen = !this.isPublishMenuOpen;
    this.isUserMenuOpen = false;
    this.isCreateMenuOpen = false;
  }

  toggleCreateMenu(event: Event): void {
    event.stopPropagation();
    this.isCreateMenuOpen = !this.isCreateMenuOpen;
    this.isUserMenuOpen = false;
    this.isPublishMenuOpen = false;
  }

  toggleExtraMenu(event: Event): void {
    event.stopPropagation();
    this.isExtraMenuOpen = !this.isExtraMenuOpen;
    // Close other dropdowns
    this.isUserMenuOpen = false;
    this.isPublishMenuOpen = false;
    this.isCreateMenuOpen = false;
  }

  closeDropdowns(): void {
    this.isUserMenuOpen = false;
    this.isPublishMenuOpen = false;
    this.isCreateMenuOpen = false;
    this.isExtraMenuOpen = false;
  }

  onSearchInput(): void {
    // TODO: Implement search functionality
  }

  navigateToSystemHealth(): void {
    this.router.navigate(['/system-health']);
  }

  // Authentication methods
  login(): void {
    this.msalService.loginRedirect();
  }

  logout(): void {
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  getUserDisplayName(): string {
    return this.currentUser?.name || this.currentUser?.username || 'User';
  }

  getUserEmail(): string {
    return this.currentUser?.username || '';
  }

  getUserInitials(): string {
    const name = this.getUserDisplayName();
    if (!name || name === 'User') {
      const email = this.getUserEmail();
      if (email) {
        return email.substring(0, 2).toUpperCase();
      }
      return 'U';
    }

    const nameParts = name.split(' ').filter(part => part.length > 0);
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return 'U';
  }

  goToUnderConstruction(): void {
    this.router.navigate(['/under-construction']);
    this.closeDropdowns();
  }

  openPrivacyNotice(): void {
    window.open(
      'https://go.microsoft.com/fwlink/?LinkId=518021',
      '_blank',
      'noopener,noreferrer'
    );
    this.isUserMenuOpen = false;
  }
}
